import type {OutlookRecipient} from '../EmailScreen/Content/EmailRecipients/useOutlookEmailRecipients';
import type {
  CompanyLink,
  DealLink,
  JobLink,
  MessageAttachment,
  PersonLink,
} from '../SendModal/EmailSync/structs';
import type {PodPostRequestFn} from '../usePodRequest';

const OUTLOOK_EMAIL_SYNCS_INCOMING_PATH =
  '/outlook_add_in/outlook_email_syncs/incoming_sync';

export type IncomingAttachmentContent = {
  id: string;
  fileName: string;
  fileContentType: string | null;
  content: string;
};

export type CreateIncomingOutlookEmailSyncParams = {
  agencyId: number;
  subject: string;
  fromAddress: string;
  internetMessageId: string | null;
  microsoftMessageId: string | null;
  microsoftConversationId: string | null;
  inboxEmailAddress: string | null;
  sentAt: string | null;
  htmlBody: string | null;
  personLinks: PersonLink[];
  companyLinks: CompanyLink[];
  jobLinks: JobLink[];
  dealLinks: DealLink[];
  recipients: OutlookRecipient[];
  attachments: MessageAttachment[];
  attachmentContents: IncomingAttachmentContent[];
};

export type CreateIncomingOutlookEmailSyncJson = {
  ok: boolean;
  errors: string[];
};

const attachmentAttributes = (
  attachmentIds: readonly string[],
  attachments: readonly MessageAttachment[]
) => {
  return attachmentIds
    .map((attachmentId) => attachments.find((att) => att.id === attachmentId))
    .filter((att): att is MessageAttachment => att != null)
    .map((att) => ({
      file_name: att.name,
      file_size: att.size,
      file_content_type: att.contentType,
    }));
};

const buildIncomingOutlookEmailSyncBody = (
  params: CreateIncomingOutlookEmailSyncParams
) => ({
  outlook_email_sync: {
    sync: true,
    subject: params.subject,
    from_address: params.fromAddress,
    internet_message_id: params.internetMessageId,
    microsoft_message_id: params.microsoftMessageId,
    microsoft_conversation_id: params.microsoftConversationId,
    inbox_email_address: params.inboxEmailAddress,
    sent_at: params.sentAt,
    html_body: params.htmlBody,
    attachments: params.attachmentContents.map((attachment) => ({
      file_name: attachment.fileName,
      file_content_type: attachment.fileContentType,
      content: attachment.content,
    })),
    outlook_email_sync_person_links_attributes: params.personLinks.map(
      (link) => ({
        person_id: link.person.id,
        pinned: link.pinned,
        outlook_email_sync_person_link_attachments_attributes:
          attachmentAttributes(link.attachmentIds, params.attachments),
      })
    ),
    outlook_email_sync_company_links_attributes: params.companyLinks.map(
      (link) => ({
        company_id: link.company.id,
        pinned: link.pinned,
        outlook_email_sync_company_link_attachments_attributes:
          attachmentAttributes(link.attachmentIds, params.attachments),
      })
    ),
    outlook_email_sync_job_links_attributes: params.jobLinks.map((link) => ({
      job_id: link.job.id,
      pinned: link.pinned,
      outlook_email_sync_job_link_attachments_attributes: attachmentAttributes(
        link.attachmentIds,
        params.attachments
      ),
    })),
    outlook_email_sync_deal_links_attributes: params.dealLinks.map((link) => ({
      deal_id: link.deal.id,
      pinned: false,
      outlook_email_sync_deal_link_attachments_attributes: attachmentAttributes(
        link.attachmentIds,
        params.attachments
      ),
    })),
    outlook_email_sync_recipients_attributes: params.recipients
      .filter((recipient) => recipient.field !== 'from')
      .map((recipient) => ({
        email_address: recipient.email,
        to: recipient.field === 'to',
        cc: recipient.field === 'cc',
        bcc: recipient.field === 'bcc',
      })),
  },
});

export const createIncomingOutlookEmailSync = async (
  postRequest: PodPostRequestFn,
  params: CreateIncomingOutlookEmailSyncParams
): Promise<CreateIncomingOutlookEmailSyncJson> => {
  const searchParams = new URLSearchParams();
  searchParams.set('agency_id', String(params.agencyId));
  const response = await postRequest(
    `${OUTLOOK_EMAIL_SYNCS_INCOMING_PATH}?${searchParams.toString()}`,
    buildIncomingOutlookEmailSyncBody(params)
  );
  const text = await response.text();
  if (!text) {
    throw new Error(`Request failed (${response.status})`);
  }
  const body = JSON.parse(text) as CreateIncomingOutlookEmailSyncJson;
  if (!response.ok) {
    const message =
      body.errors?.length > 0
        ? body.errors.join(', ')
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return body;
};
